import { useState, useEffect } from "react";
import { useSession } from "next-auth/client";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Button,
  Text,
  Box,
  Flex,
  Grid,
  GridItem,
  FormControl,
  FormLabel,
  Checkbox,
  VStack,
  Input,
  useDisclosure,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  SliderMark,
} from "@chakra-ui/react";
import toast from "react-hot-toast";
import { Step, Steps, useSteps } from "chakra-ui-steps";
import { IoIosReturnRight, IoMdOptions } from "react-icons/io";
import { v4 as uuid } from "uuid";
import { ContactListDropdown } from "components/contact-list-dropdown";
import { InputWithList } from "components/input-with-list.tsx";
import { AddIcon } from "@chakra-ui/icons";
import { getAccount, updateUser } from "utils/sessionHelper";
import { firebaseClient } from "firebaseClient";
import { AiOutlineSelect } from "react-icons/ai";
import { DynamicRangeSlider } from "components/dynamic-range-slider";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onAddSuccess?: () => void;
  watcher?: any;
}

const MAX_FOLLOWERS = 105000;

export function CreateWatcher({
  isOpen,
  onClose,
  onAddSuccess = () => {},
  watcher = undefined,
}: Props) {
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [watcherName, setWatcherName] = useState<string>("");
  const [inputContactList, setInputContactList] = useState<any>();
  const [outputContactList, setOutputContactList] = useState<any>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [includeList, setIncludeList] = useState<string[]>([]);
  const [excludeList, setExcludeList] = useState<string[]>([]);
  const [excludeCountryList, setExcludeCountryList] = useState<string[]>([]);
  const [followers, setFollowers] = useState<any[]>([0, 999999999]);
  // const [excludeFollowers, setExcludeFollowers] = useState<boolean>(false);
  // const [excludeFollows, setExcludeFollows] = useState<boolean>(false);
  const [newListName, setNewListName] = useState<string>("");
  const [threshold, setThreshold] = useState<number>(-5);
  const [, refresher] = useState({});
  const [isCreatingNewList, setIsCreatingNewList] = useState<boolean>(false);
  const {
    isOpen: isOpenCreateNewList,
    onToggle: onToggleCreateNewList,
    onClose: onCloseCreateNewList,
  } = useDisclosure();
  const { nextStep, prevStep, activeStep, setStep } = useSteps({
    initialStep: 0,
  });
  const [session] = useSession();

  const stepOneUI = (
    <Box mt="6" mb={{ base: "10", md: "0" }} textAlign="left">
      <Text color="gray.500">
        Watchers consist of a set of criterias and information that help guide
        our AI to collect the right leads for you. In the first step, you pick a
        Contacts List that you've setup in Tweet Hunter to serve as an initial
        input for the AI.
      </Text>

      <Box mt="10">
        <Text fontWeight="semibold" fontSize="lg">
          Name
        </Text>
        <Input
          size="sm"
          placeholder="SaaS Makers"
          borderRadius="lg"
          w="300px"
          mt="2"
          value={watcherName}
          onChange={(e) => setWatcherName(e.target.value)}
        />
      </Box>

      <Box mt="8">
        <Text fontWeight="semibold" fontSize="lg">
          Select a Contacts List
        </Text>
        <Text color="gray.500" fontSize="sm">
          Note: a good list for the Lead Finder contains 15 to 25 contacts.
        </Text>
        <ContactListDropdown
          value={inputContactList}
          placeholder="Pick from contact list"
          onSelectList={(list) => setInputContactList(list)}
          menuButtonProps={{
            ml: 0,
            mt: 4,
          }}
        />
      </Box>
    </Box>
  );

  const stepTwoUI = (
    <Box mt="6" mb={{ base: "10", md: "0" }} textAlign="left">
      <Text color="gray.500">
        Add criterias for the leads you want us to find for you.
      </Text>
      <Box mt="8">
        <Text fontWeight="semibold">Additional criterias</Text>
        <Text fontSize="sm" color="gray.500" mt="1">
          Add additional criterias to make the leads as relevant as possible for
          you.
        </Text>
        <Grid
          templateColumns={{ base: "repeat(1, 1fr)", md: "repeat(2, 1fr)" }}
          gap={{ base: "8", md: "12" }}
          mx="0"
        >
          <GridItem>
            <Box mt="10">
              <Text fontWeight="medium">Bio Keywords</Text>
              <Flex
                mt="4"
                flexDir={{ base: "column", md: "row" }}
                justifyContent="space-between"
              >
                <Text fontSize="sm" mt="1" mb={{ base: "2", md: "0" }}>
                  Must contain
                </Text>
                <InputWithList
                  helperText="We check for every word you add here"
                  onChange={(list) => setIncludeList(list)}
                  initValue={includeList}
                  buttonText="Include"
                  mainContainerProps={{
                    ml: { base: "0", md: "3" },
                  }}
                  inputProps={{
                    width: { base: "240px", md: "170px" },
                  }}
                />
              </Flex>
              <Flex
                mt="6"
                flexDir={{ base: "column", md: "row" }}
                justifyContent="space-between"
              >
                <Text fontSize="sm" mt="1" mb={{ base: "2", md: "0" }}>
                  Does not contain
                </Text>
                <InputWithList
                  onChange={(list) => setExcludeList(list)}
                  initValue={excludeList}
                  buttonText="Exclude"
                  mainContainerProps={{
                    ml: { base: "0", md: "3" },
                  }}
                  inputProps={{
                    width: { base: "240px", md: "170px" },
                  }}
                />
              </Flex>
            </Box>
            <Box mt="10">
              <FormControl width={{ base: "100%", md: "80%" }}>
                <FormLabel>Followers</FormLabel>
                <DynamicRangeSlider
                  labelTextPrefix="followers"
                  range={[0, 500, 1000, 5000, 20000, 50000, 100000, 999999999]}
                  defaultValue={followers}
                  displayMaxAsUnlimited={true}
                  onChangeEnd={(value) => setFollowers(value)}
                />
              </FormControl>
            </Box>
          </GridItem>
          <GridItem>
            <Box mt="10">
              <Text fontWeight="medium">Exclude countries</Text>
              <Text as="i" color="gray.500" fontSize="sm">
                Works only if country is mentioned on profile
              </Text>
              <InputWithList
                onChange={(list) => setExcludeCountryList(list)}
                initValue={excludeCountryList}
                buttonText="Exclude"
                mainContainerProps={{
                  ml: 0,
                  mt: 4,
                }}
                inputProps={{
                  width: { base: "240px", md: "170px" },
                }}
              />
            </Box>
            {/* <VStack mt="10" spacing={3} alignItems="flex-start">
              <Checkbox
                isChecked={excludeFollowers}
                onChange={(e) => setExcludeFollowers(e.target.checked)}
              >
                Exclude my followers
              </Checkbox>
              <Checkbox
                isChecked={excludeFollows}
                onChange={(e) => setExcludeFollows(e.target.checked)}
              >
                Exclude people I follow
              </Checkbox>
            </VStack> */}
            <Box mt="10">
              <Text fontWeight="medium">Precision grade</Text>
              <Slider
                aria-label="leads threshold"
                mt="4"
                min={-5}
                max={5}
                defaultValue={0}
                value={threshold}
                step={1}
                onChange={(v) => setThreshold(v)}
              >
                <SliderMark value={-5} mt="3">
                  lot of leads
                </SliderMark>
                <SliderMark value={2.5} mt="3">
                  very targeted leads
                </SliderMark>
                <SliderTrack>
                  <SliderFilledTrack />
                </SliderTrack>
                <SliderThumb />
              </Slider>
            </Box>
          </GridItem>
        </Grid>
      </Box>
    </Box>
  );

  const stepThreeUI = (
    <Box mt="6" mb={{ base: "10", md: "0" }} textAlign="left">
      <Text color="gray.500">
        Pick an existing Contacts list or create a new one. This is where we'll
        save your newly found leads.
      </Text>
      <Box mt="8">
        <Text fontWeight="semibold" fontSize="lg">
          Output list
        </Text>
        <Text color="gray.500" fontSize="sm">
          Pick or create a list where identified leads should be exported.
        </Text>
        <ContactListDropdown
          value={outputContactList}
          placeholder="Pick from contact list"
          onSelectList={(list) => setOutputContactList(list)}
          menuButtonProps={{
            ml: 0,
            mt: 4,
          }}
        />
        <Box mt="6">
          <Button
            color="gray.400"
            size="sm"
            variant="tertiary"
            leftIcon={<AddIcon />}
            onClick={onToggleCreateNewList}
          >
            Create a new empty list
          </Button>
          {isOpenCreateNewList && (
            <Flex mt="5" flexDir={{ base: "column", md: "row" }}>
              <Input
                w={{ base: "100%", md: "300px" }}
                size="sm"
                borderRadius="md"
                value={newListName}
                placeholder="Indie Hackers"
                onChange={(e) => setNewListName(e.target.value)}
              />
              <Button
                ml={{ base: "0", md: "3" }}
                mt={{ base: "2", md: "0" }}
                size="sm"
                width="min-content"
                onClick={handleCreateList}
                isLoading={isCreatingNewList}
              >
                Create List
              </Button>
            </Flex>
          )}
        </Box>
      </Box>
    </Box>
  );

  async function handleCreateList() {
    if (!newListName) {
      toast.error("Please write a valid list name");
      return;
    }

    setIsCreatingNewList(true);
    let contactLists = getAccount(session)?.contactLists
      ? [...getAccount(session).contactLists]
      : [];

    const newListid =
      getAccount(session).contactLists &&
      getAccount(session).contactLists?.length > 0
        ? Math.max(...getAccount(session).contactLists.map((x) => x.id)) + 1
        : 0;

    const newList = {
      name: newListName,
      id: newListid,
    };
    contactLists.push(newList);

    await updateUser(session, { contactLists });
    setOutputContactList(newList);
    onCloseCreateNewList();
    setNewListName("");
    refresher({});
    setIsCreatingNewList(false);
  }

  const steps = [
    {
      label: "Set input list",
      icon: AiOutlineSelect,
      content: stepOneUI,
    },
    {
      label: "Select criteria (optional)",
      icon: IoMdOptions,
      content: stepTwoUI,
    },
    {
      label: "Add output list",
      icon: IoIosReturnRight,
      content: stepThreeUI,
    },
  ];

  const handleNext = async () => {
    if (!isLastStep) {
      nextStep();
      return;
    }

    let missingItems: string[] = [];

    if (!watcherName) {
      missingItems.push("Watcher name");
    }
    if (!inputContactList) {
      missingItems.push("Input list");
    }
    if (!outputContactList) {
      missingItems.push("Output list");
    }

    if (missingItems?.length > 0) {
      const missingItemsLength = missingItems?.length;
      let missingItemString = "";
      if (missingItems?.length === 1) {
        missingItemString = missingItems[0];
      } else {
        const lastItem = missingItems.pop();
        missingItemString = missingItems.join(", ") + ` and ${lastItem}`;
      }
      toast.error(
        `${missingItemString} ${
          missingItemsLength === 1 ? "is" : "are"
        } required`
      );
      return;
    }

    setIsLoading(true);
    let newWatcher: any = {
      name: watcherName,
      listLeads: outputContactList.id,
      listSample: inputContactList.id,
      filters: {
        nbMinFollowers: followers[0],
        nbMaxFollowers: followers[1],
        // noFollowers: excludeFollowers,
        // noFollowing: excludeFollows,
        containInBio: includeList,
        notContainInBio: excludeList,
        notFromCountries: excludeCountryList,
        evidenceThreshold: threshold,
      },
    };

    const db = firebaseClient.firestore();
    const query = db
      .collection("users")
      .doc(getAccount(session)?.id)
      .collection("leadWatchers");

    if (isEditMode) {
      await query.doc(watcher.id).update(newWatcher);
    } else {
      const id = uuid();
      newWatcher = {
        ...newWatcher,
        id,
        leadsFound: 0,
        status: "running",
        idUser: getAccount(session).id,
      };
      await query.doc(id).set(newWatcher);
    }

    toast.success("Watcher added!");
    clearInputs();
    onAddSuccess();
  };

  const clearInputs = () => {
    setStep(0);
    setWatcherName("");
    setIsLoading(false);
    setInputContactList(null);
    setOutputContactList(null);
    setIncludeList([]);
    setExcludeList([]);
    setExcludeCountryList([]);
    // setExcludeFollowers(false);
    // setExcludeFollows(false);
    setIsEditMode(false);
    onCloseCreateNewList();
    setNewListName("");
  };

  const handleModalClose = () => {
    clearInputs();
    onClose();
  };

  useEffect(() => {
    if (session && isOpen && watcher) {
      setIsEditMode(true);
      // setExcludeFollows(watcher.filters.noFollowing);
      // setExcludeFollowers(watcher.filters.noFollowers);
      setFollowers([
        watcher.filters.nbMinFollowers,
        watcher.filters.nbMaxFollowers,
      ]);
      setExcludeCountryList(watcher.filters.notFromCountries);
      setExcludeList(watcher.filters.notContainInBio);
      setIncludeList(watcher.filters.containInBio);
      setThreshold(watcher.filters?.evidenceThreshold ?? 0);

      const contactLists = getAccount(session)?.contactLists;
      const outputList = contactLists.find((c) => c.id === watcher.listLeads);
      const inputList = contactLists.find((c) => c.id === watcher.listSample);

      setInputContactList(inputList);
      setOutputContactList(outputList);
      setWatcherName(watcher.name);
    }
  }, [isOpen, session]);

  const isLastStep = activeStep >= steps?.length - 1;

  return (
    <Modal size="4xl" isOpen={isOpen} onClose={handleModalClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton />
        <ModalHeader fontWeight="semibold">
          {isEditMode ? `Editing ${watcher.name}` : "Create a new watcher"}
        </ModalHeader>
        <ModalBody display="flex" flexDirection="column" width="100%">
          <Steps
            state={undefined}
            activeStep={activeStep}
            onClickStep={(step) => setStep(step)}
          >
            {steps.map(({ label, icon, content }) => (
              <Step label={label} key={label} icon={icon}>
                {content}
              </Step>
            ))}
          </Steps>

          <Flex width="100%" mt="6" mb="2" justify="flex-end">
            <Button
              isDisabled={activeStep === 0 || isLoading}
              mr={4}
              onClick={prevStep}
              variant="tertiary"
            >
              Prev
            </Button>
            <Button
              variant={"primary"}
              onClick={handleNext}
              isLoading={isLoading}
            >
              {isLastStep
                ? isEditMode
                  ? "Update Watcher"
                  : "Create Watcher"
                : "Next"}
            </Button>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
